/**
 * Custom Reviews Widget
 * Handles review display, filtering, sorting, pagination, submission, and photo upload.
 */

class CustomReviewsWidget extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.productTitle = this.dataset.productTitle;
    this.apiBase = 'https://review-worker.ryan-29b.workers.dev';
    this.turnstileSiteKey = this.dataset.turnstileSiteKey || '';

    this.state = {
      reviews: [],
      stats: null,
      page: 1,
      totalPages: 1,
      sort: 'newest',
      rating: null,
      photos: false,
      search: '',
      loading: true,
      uploadedPhotos: [],
      formRating: 0,
    };

    this.init();
  }

  async init() {
    await this.loadStats();
    this.loadAiSummary();
    await this.loadReviews();
    this.bindEvents();
    this.maybeAutoOpen(); // CUSTOM_REVIEWS_BY_CLAUDE — open form when arriving via a shared link
  }

  async loadStats() {
    try {
      const res = await fetch(`${this.apiBase}/reviews/stats/${this.productId}`);
      if (res.ok) this.state.stats = await res.json();
    } catch { /* stats optional */ }
    this.renderSummary();
    this.updateTabTitle();
    this.toggleToolbar();
  }

  async loadReviews() {
    this.state.loading = true;
    this.renderLoading();

    const params = new URLSearchParams({
      page: this.state.page,
      sort: this.state.sort,
    });
    if (this.state.rating) params.set('rating', this.state.rating);
    if (this.state.photos) params.set('photos', 'true');
    if (this.state.search) params.set('q', this.state.search);

    try {
      const res = await fetch(`${this.apiBase}/reviews/${this.productId}?${params}`);
      if (res.ok) {
        const data = await res.json();
        this.state.reviews = data.reviews;
        this.state.page = data.pagination.page;
        this.state.totalPages = data.pagination.totalPages;
      }
    } catch { /* handled by empty state */ }

    this.state.loading = false;
    this.renderReviews();
    this.renderPagination();
  }

  renderSummary() {
    const container = this.querySelector('.cr-summary');
    if (!container || !this.state.stats) return;

    const s = this.state.stats;
    container.querySelector('.cr-summary__number').textContent = s.average || '0';

    const starsContainer = container.querySelector('.cr-summary__stars');
    if (starsContainer) {
      starsContainer.innerHTML = this.renderStarsSVG(s.average, 22);
    }

    container.querySelector('.cr-summary__total').textContent =
      `${s.count} ${s.count === 1 ? 'review' : 'reviews'}`;

    for (let i = 5; i >= 1; i--) {
      const count = s.distribution[i] || 0;
      const pct = s.count > 0 ? (count / s.count) * 100 : 0;
      const row = container.querySelector(`[data-star="${i}"]`);
      if (row) {
        row.querySelector('.cr-histogram__fill').style.width = `${pct}%`;
        row.querySelector('.cr-histogram__count').textContent = count;
      }
    }
  }

  toggleToolbar() {
    const count = this.state.stats?.count || 0;
    const toolbar = this.querySelector('.cr-toolbar');
    const summary = this.querySelector('.cr-summary');
    const writeBtn = this.querySelector('.cr-write-btn');
    if (toolbar) toolbar.style.display = count > 0 ? '' : 'none';
    if (summary) summary.style.display = count > 0 ? '' : 'none';
    if (writeBtn) writeBtn.style.display = count > 0 ? '' : 'none';
  }

  async loadAiSummary() {
    try {
      const res = await fetch(`${this.apiBase}/reviews/summary/${this.productId}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.summary) this.renderAiSummary(data.summary);
    } catch {}
  }

  renderAiSummary(text) {
    const existing = this.querySelector('.cr-ai-summary');
    if (existing) existing.remove();
    const toolbar = this.querySelector('.cr-toolbar');
    if (!toolbar) return;
    const card = document.createElement('div');
    card.className = 'cr-ai-summary';
    card.innerHTML = `<p class="cr-ai-summary__heading">What customers are saying</p><p class="cr-ai-summary__text">${text}</p>`;
    toolbar.insertAdjacentElement('beforebegin', card);
  }

  updateTabTitle() {
    const count = this.state.stats?.count || 0;
    const details = this.closest('details');
    if (details) {
      const title = details.querySelector('.disclosure__title');
      if (title) title.textContent = `Reviews (${count})`;
      return;
    }
    const panel = this.closest('[role="tabpanel"]');
    if (panel) {
      const tabId = panel.getAttribute('aria-labelledby');
      if (tabId) {
        const tab = document.getElementById(tabId);
        if (tab) {
          const span = tab.querySelector('span') || tab;
          span.textContent = `Reviews (${count})`;
        }
      }
    }
  }

  renderStarsSVG(rating, size) {
    if (!this._starUid) this._starUid = 0;
    let html = '';
    for (let i = 1; i <= 5; i++) {
      const fillPct = Math.min(100, Math.max(0, (rating - i + 1) * 100));
      const gradId = `cr-g-${this._starUid++}`;
      html += `<svg width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true">
        <defs><linearGradient id="${gradId}" x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="${fillPct}%" stop-color="#F5A623"/>
          <stop offset="${fillPct}%" stop-color="transparent"/>
        </linearGradient></defs>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
          fill="url(#${gradId})" stroke="#F5A623" stroke-width="1.5" stroke-linejoin="round"/>
      </svg>`;
    }
    return html;
  }

  renderLoading() {
    const list = this.querySelector('.cr-reviews');
    if (list) list.innerHTML = '<div class="cr-loading"><div class="cr-spinner"></div></div>';
  }

  renderReviews() {
    const list = this.querySelector('.cr-reviews');
    if (!list) return;

    if (this.state.reviews.length === 0) {
      const hasFilters = this.state.rating || this.state.photos || this.state.search;
      if (hasFilters) {
        list.innerHTML = `
          <div class="cr-empty">
            <p class="cr-empty__heading">No matching reviews</p>
            <p class="cr-empty__text">Try adjusting your filters.</p>
          </div>`;
      } else {
        list.innerHTML = `
          <div class="cr-empty">
            <p class="cr-empty__heading">No reviews yet</p>
            <p class="cr-empty__text">Be the first to review this product and help other customers.</p>
            <button type="button" class="btn btn--primary cr-empty__btn" onclick="this.closest('custom-reviews-widget').querySelector('.cr-write-btn').click()">Leave a Review</button>
          </div>`;
      }
      return;
    }

    list.innerHTML = this.state.reviews.map(r => this.renderReviewCard(r)).join('');
  }

  renderReviewCard(r) {
    const date = new Date(r.created_at);
    const dateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const stars = this.renderStarsSVG(r.rating, 16);

    let photos = '';
    if (r.photo_urls && r.photo_urls.length > 0) {
      photos = `<div class="cr-review__photos">
        ${r.photo_urls.map(url => `<img src="${this.escapeAttr(url)}" class="cr-review__photo" loading="lazy" alt="Review photo" data-lightbox>`).join('')}
      </div>`;
    }

    /* CUSTOM_REVIEWS_BY_CLAUDE (Ryan 2026-10-25) - our reply sits closed under a small toggle (the same as
       the site-wide slide-out), and a Google-translated review shows the translation with the original
       behind "Show original". The worker returns them split; splitTranslation also reads the old shape. */
    let reply = '';
    const replyText = this.tidyText(r.admin_reply);
    if (replyText) {
      reply = `<div class="cr-review__reply-wrap">
        <button type="button" class="cr-review__reply-toggle" aria-expanded="false">Reply from Blue Sky Fitness Supply <span class="cr-review__reply-chev" aria-hidden="true">&#9656;</span></button>
        <div class="cr-review__reply" hidden>
          <p class="cr-review__reply-text">${this.escapeHtml(replyText)}</p>
        </div>
      </div>`;
    }
    const t = this.splitTranslation(r);
    const translated = t.original
      ? `<div class="cr-review__translated">Translated by Google &middot; <button type="button" class="cr-review__original-toggle">Show original</button></div>
      <p class="cr-review__original" hidden>${this.escapeHtml(t.original)}</p>`
      : '';

    const location = r.customer_state ? ` from ${this.escapeHtml(r.customer_state)}` : '';

    return `<div class="cr-review" data-review-id="${r.id}">
      <div class="cr-review__header">
        <div class="cr-stars__icons" style="--cr-star-color: #F5A623;">${stars}</div>
        <span class="cr-review__author">${this.escapeHtml(r.customer_name)}${location}</span>
        ${r.verified_purchase ? '<span class="cr-review__verified"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>Verified Buyer</span>' : ''}
        <span class="cr-review__date">${dateStr}</span>
      </div>
      ${r.title ? `<div class="cr-review__title">${this.escapeHtml(r.title)}</div>` : ''}
      <p class="cr-review__body">${this.escapeHtml(t.body)}</p>
      ${translated}
      ${photos}
      ${reply}
      <div class="cr-review__footer">
        <span class="cr-review__helpful-label">Helpful?</span>
        <button class="cr-vote-btn" data-vote="1" data-review="${r.id}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 9V5a3 3 0 00-6 0v4H5a2 2 0 00-2 2v7a2 2 0 002 2h14l-5-11z"/></svg>
          ${r.helpful_up || 0}
        </button>
        <button class="cr-vote-btn" data-vote="-1" data-review="${r.id}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="transform:rotate(180deg)"><path d="M14 9V5a3 3 0 00-6 0v4H5a2 2 0 00-2 2v7a2 2 0 002 2h14l-5-11z"/></svg>
          ${r.helpful_down || 0}
        </button>
      </div>
    </div>`;
  }

  tidyText(str) {
    return String(str || '').replace(/\r\n?/g, '\n').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  }

  splitTranslation(r) {
    if (r.translated && r.body_original) return { body: this.tidyText(r.body), original: this.tidyText(r.body_original) };
    const s = this.tidyText(r.body);
    const m = /^\(Translated by Google\)\s*([\s\S]*?)\s*\(Original\)\s*([\s\S]*)$/.exec(s);
    if (m && m[1] && m[2]) return { body: this.tidyText(m[1]), original: this.tidyText(m[2]) };
    return { body: s, original: '' };
  }
  /* END CUSTOM_REVIEWS_BY_CLAUDE */

  renderPagination() {
    const container = this.querySelector('.cr-pagination');
    if (!container) return;

    if (this.state.totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = `
      <button class="cr-pagination__btn" data-page="prev" ${this.state.page <= 1 ? 'disabled' : ''}>Previous</button>
      <span class="cr-pagination__info">Page ${this.state.page} of ${this.state.totalPages}</span>
      <button class="cr-pagination__btn" data-page="next" ${this.state.page >= this.state.totalPages ? 'disabled' : ''}>Next</button>
    `;
  }

  bindEvents() {
    // Sort
    const sort = this.querySelector('.cr-toolbar__sort');
    if (sort) sort.addEventListener('change', () => {
      this.state.sort = sort.value;
      this.state.page = 1;
      this.loadReviews();
    });

    // Filter by star
    const filter = this.querySelector('.cr-toolbar__filter');
    if (filter) filter.addEventListener('change', () => {
      this.state.rating = filter.value || null;
      this.state.page = 1;
      this.loadReviews();
    });

    // Photos toggle
    const photosBtn = this.querySelector('.cr-toolbar__photos-toggle');
    if (photosBtn) photosBtn.addEventListener('click', () => {
      this.state.photos = !this.state.photos;
      photosBtn.classList.toggle('is-active', this.state.photos);
      this.state.page = 1;
      this.loadReviews();
    });

    // Search
    const search = this.querySelector('.cr-toolbar__search');
    if (search) {
      let searchTimeout;
      search.addEventListener('input', () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          this.state.search = search.value.trim();
          this.state.page = 1;
          this.loadReviews();
        }, 400);
      });
    }

    // Clear filters
    this.addEventListener('click', (e) => {
      if (e.target.closest('.cr-toolbar__clear')) {
        this.state.rating = null;
        this.state.photos = false;
        this.state.search = '';
        this.state.page = 1;
        const s = this.querySelector('.cr-toolbar__sort');
        const f = this.querySelector('.cr-toolbar__filter');
        const q = this.querySelector('.cr-toolbar__search');
        const p = this.querySelector('.cr-toolbar__photos-toggle');
        if (s) s.value = 'newest';
        if (f) f.value = '';
        if (q) q.value = '';
        if (p) p.classList.remove('is-active');
        this.state.sort = 'newest';
        this.loadReviews();
      }
    });

    // Histogram click
    this.addEventListener('click', (e) => {
      const row = e.target.closest('.cr-histogram__row');
      if (!row) return;
      const star = row.dataset.star;
      const filter = this.querySelector('.cr-toolbar__filter');
      if (this.state.rating === star) {
        this.state.rating = null;
        row.classList.remove('is-active');
        if (filter) filter.value = '';
      } else {
        this.querySelectorAll('.cr-histogram__row').forEach(r => r.classList.remove('is-active'));
        this.state.rating = star;
        row.classList.add('is-active');
        if (filter) filter.value = star;
      }
      this.state.page = 1;
      this.loadReviews();
    });

    // Pagination
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.cr-pagination__btn');
      if (!btn || btn.disabled) return;
      if (btn.dataset.page === 'prev') this.state.page--;
      else if (btn.dataset.page === 'next') this.state.page++;
      this.loadReviews();
      (this.querySelector('.cr-summary') || this).scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // Voting
    this.addEventListener('click', async (e) => {
      const btn = e.target.closest('.cr-vote-btn');
      if (!btn || btn.classList.contains('is-voted')) return;
      const reviewId = btn.dataset.review;
      const vote = parseInt(btn.dataset.vote, 10);
      try {
        const res = await fetch(`${this.apiBase}/reviews/vote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reviewId, vote }),
        });
        if (res.ok) {
          btn.classList.add('is-voted');
          const countEl = btn.lastChild;
          if (countEl) countEl.textContent = ` ${parseInt(countEl.textContent.trim(), 10) + 1}`;
        }
      } catch { /* silent */ }
    });

    // CUSTOM_REVIEWS_BY_CLAUDE (Ryan 2026-10-25) - our reply and the original text open from their toggles
    this.addEventListener('click', (e) => {
      const rt = e.target.closest('.cr-review__reply-toggle');
      if (rt) {
        const box = rt.nextElementSibling;
        if (!box) return;
        box.hidden = !box.hidden;
        rt.setAttribute('aria-expanded', box.hidden ? 'false' : 'true');
        return;
      }
      const ot = e.target.closest('.cr-review__original-toggle');
      if (ot) {
        const p = ot.parentNode.nextElementSibling;
        if (p) { p.hidden = !p.hidden; ot.textContent = p.hidden ? 'Show original' : 'Hide original'; }
      }
    });

    // Photo lightbox
    this.addEventListener('click', (e) => {
      if (e.target.matches('[data-lightbox]')) {
        this.openLightbox(e.target.src);
      }
    });

    // Write review button
    const writeBtn = this.querySelector('.cr-write-btn');
    if (writeBtn) writeBtn.addEventListener('click', () => this.openForm());

    // CUSTOM_REVIEWS_BY_CLAUDE — copy shareable review link
    const shareBtn = this.querySelector('.cr-share-btn');
    if (shareBtn) shareBtn.addEventListener('click', () => this.copyShareLink());
    // END CUSTOM_REVIEWS_BY_CLAUDE

    // Form close — only via the X button. Clicking the overlay does NOT close the
    // form, so a half-written review isn't lost to an accidental outside click.
    this.addEventListener('click', (e) => {
      if (e.target.closest('.cr-form__close')) {
        this.closeForm();
      }
    });

    // Star select in form
    this.addEventListener('click', (e) => {
      const starBtn = e.target.closest('.cr-form__star-btn');
      if (!starBtn) return;
      this.state.formRating = parseInt(starBtn.dataset.star, 10);
      this.updateFormStars();
    });

    // Star hover preview
    this.addEventListener('mouseover', (e) => {
      const starBtn = e.target.closest('.cr-form__star-btn');
      if (!starBtn) return;
      const hoverRating = parseInt(starBtn.dataset.star, 10);
      this.querySelectorAll('.cr-form__star-btn').forEach((btn) => {
        btn.classList.toggle('is-filled', parseInt(btn.dataset.star, 10) <= hoverRating);
      });
    });

    const starSelect = this.querySelector('.cr-form__star-select');
    if (starSelect) {
      starSelect.addEventListener('mouseleave', () => this.updateFormStars());
    }

    // Character counter
    const bodyInput = this.querySelector('.cr-form__body');
    const charCount = this.querySelector('.cr-form__char-count');
    if (bodyInput && charCount) {
      bodyInput.addEventListener('input', () => {
        const len = bodyInput.value.length;
        if (len === 0) {
          charCount.textContent = '0/5000';
          charCount.classList.remove('is-error');
        } else if (len < 50) {
          charCount.textContent = `${50 - len} more characters needed`;
          charCount.classList.add('is-error');
        } else {
          charCount.textContent = `${len}/5000`;
          charCount.classList.toggle('is-error', len > 5000);
        }
      });
    }

    // Photo upload
    const photoInput = this.querySelector('.cr-form__photo-input');
    const photoBtn = this.querySelector('.cr-form__photo-btn');
    if (photoBtn && photoInput) {
      photoBtn.addEventListener('click', () => photoInput.click());
      photoInput.addEventListener('change', () => this.handlePhotoUpload(photoInput));
    }

    // Form submit
    const form = this.querySelector('.cr-form');
    if (form) form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitReview();
    });

    // Escape key — closes the photo lightbox only. The review form is intentionally
    // NOT dismissed on Escape; the X button is the only way to close it.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeLightbox();
      }
    });
  }

  updateFormStars() {
    this.querySelectorAll('.cr-form__star-btn').forEach((btn) => {
      btn.classList.toggle('is-filled', parseInt(btn.dataset.star, 10) <= this.state.formRating);
    });
  }

  openForm() {
    this.querySelector('.cr-form-overlay')?.classList.add('is-open');
    this.querySelector('.cr-form-modal')?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeForm() {
    this.querySelector('.cr-form-overlay')?.classList.remove('is-open');
    this.querySelector('.cr-form-modal')?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  // CUSTOM_REVIEWS_BY_CLAUDE — shareable review link (copy + auto-open) ──────────
  // Opens the review form automatically when the page is reached via a shared
  // link (e.g. /products/x?write-review=1), then strips the param so a refresh
  // doesn't re-trigger the popup.
  maybeAutoOpen() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch { return; }
    if (params.get('write-review') !== '1') return;

    // The widget usually lives inside a product-details tab/accordion. The form
    // modal is position:fixed, so it won't render while an ancestor is hidden —
    // reveal the panel first, then open.
    const details = this.closest('details');
    if (details) details.open = true;

    const panel = this.closest('[role="tabpanel"]');
    if (panel) {
      const tab = panel.getAttribute('aria-labelledby') &&
        document.getElementById(panel.getAttribute('aria-labelledby'));
      if (tab && tab.getAttribute('aria-selected') !== 'true' &&
          tab.getAttribute('aria-expanded') !== 'true') {
        tab.click();
      }
    }

    // Defer so the revealed panel has painted before we open the modal. We do NOT
    // scroll to the widget — the modal is fixed/centered, so keeping the page at the
    // top leaves the product name/image visible (dimmed) behind the overlay, which
    // confirms to the reviewer which product they're reviewing.
    setTimeout(() => {
      this.openForm();
    }, 80);

    params.delete('write-review');
    const qs = params.toString();
    const clean = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    try { history.replaceState(null, '', clean); } catch { /* non-fatal */ }
  }

  // Copies a link that, when opened, lands on this product with the form open.
  copyShareLink() {
    if (!this.productHandle) return;
    const url = `${window.location.origin}/products/${this.productHandle}?write-review=1`;
    const done = () => this.showShareToast('Link copied!');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => this.fallbackCopy(url, done));
    } else {
      this.fallbackCopy(url, done);
    }
  }

  fallbackCopy(text, onDone) {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      onDone();
    } catch { /* clipboard unavailable — silent */ }
  }

  showShareToast(msg) {
    const toast = this.querySelector('.cr-share-toast');
    if (!toast) return;
    toast.textContent = msg;
    toast.hidden = false;
    void toast.offsetWidth; // force reflow so the transition runs after unhide
    toast.classList.add('is-visible');
    clearTimeout(this._shareToastTimer);
    this._shareToastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => { toast.hidden = true; }, 200);
    }, 2000);
  }
  // END CUSTOM_REVIEWS_BY_CLAUDE

  openLightbox(src) {
    const clickedImg = this.querySelector(`[data-lightbox][src="${CSS.escape(src)}"]`);
    const reviewCard = clickedImg?.closest('.cr-review');
    const allPhotos = reviewCard ? Array.from(reviewCard.querySelectorAll('[data-lightbox]')).map(img => img.src) : [src];
    this._lbPhotos = allPhotos;
    this._lbIndex = Math.max(0, allPhotos.indexOf(src));

    let lightbox = this.querySelector('.cr-lightbox');
    if (!lightbox) {
      lightbox = document.createElement('div');
      lightbox.className = 'cr-lightbox';
      lightbox.innerHTML = `
        <button class="cr-lightbox__close" aria-label="Close">&times;</button>
        <button class="cr-lightbox__prev" aria-label="Previous">&#8249;</button>
        <button class="cr-lightbox__next" aria-label="Next">&#8250;</button>
        <img class="cr-lightbox__img" src="" alt="Review photo">
        <span class="cr-lightbox__counter"></span>
      `;
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox || e.target.closest('.cr-lightbox__close')) this.closeLightbox();
        else if (e.target.closest('.cr-lightbox__prev')) this._lbNav(-1);
        else if (e.target.closest('.cr-lightbox__next')) this._lbNav(1);
      });
      this._lbKeyHandler = (e) => {
        if (!this.querySelector('.cr-lightbox.is-open')) return;
        if (e.key === 'Escape') this.closeLightbox();
        else if (e.key === 'ArrowLeft') this._lbNav(-1);
        else if (e.key === 'ArrowRight') this._lbNav(1);
      };
      document.addEventListener('keydown', this._lbKeyHandler);
      let touchStartX = 0;
      lightbox.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
      lightbox.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 50) this._lbNav(dx < 0 ? 1 : -1);
      });
      this.appendChild(lightbox);
    }
    this._lbUpdate(lightbox);
    lightbox.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  _lbNav(dir) {
    if (!this._lbPhotos || this._lbPhotos.length <= 1) return;
    this._lbIndex = (this._lbIndex + dir + this._lbPhotos.length) % this._lbPhotos.length;
    this._lbUpdate(this.querySelector('.cr-lightbox'));
  }

  _lbUpdate(lightbox) {
    if (!lightbox) return;
    lightbox.querySelector('.cr-lightbox__img').src = this._lbPhotos[this._lbIndex];
    const counter = lightbox.querySelector('.cr-lightbox__counter');
    const multi = this._lbPhotos.length > 1;
    counter.textContent = multi ? `${this._lbIndex + 1} of ${this._lbPhotos.length}` : '';
    lightbox.querySelector('.cr-lightbox__prev').style.display = multi ? '' : 'none';
    lightbox.querySelector('.cr-lightbox__next').style.display = multi ? '' : 'none';
  }

  closeLightbox() {
    const lightbox = this.querySelector('.cr-lightbox');
    if (lightbox) {
      lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  async handlePhotoUpload(input) {
    const files = Array.from(input.files);
    if (this.state.uploadedPhotos.length + files.length > 5) {
      this.showFormError('Maximum 5 photos allowed.');
      return;
    }

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        this.showFormError(`${file.name} is too large. Maximum 10MB per photo.`);
        continue;
      }

      try {
        const res = await fetch(`${this.apiBase}/reviews/upload`, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (res.ok) {
          const data = await res.json();
          this.state.uploadedPhotos.push(data.url);
          this.renderPhotoPreview();
        } else {
          const err = await res.json();
          this.showFormError(err.error || 'Photo upload failed.');
        }
      } catch {
        this.showFormError('Photo upload failed. Please try again.');
      }
    }
    input.value = '';
  }

  renderPhotoPreview() {
    const container = this.querySelector('.cr-form__photo-preview');
    if (!container) return;
    container.innerHTML = this.state.uploadedPhotos.map((url, i) => `
      <div class="cr-form__photo-thumb">
        <img src="${this.escapeAttr(url)}" alt="Upload ${i + 1}">
        <button type="button" class="cr-form__photo-remove" data-photo-idx="${i}">&times;</button>
      </div>
    `).join('');

    container.querySelectorAll('.cr-form__photo-remove').forEach(btn => {
      btn.addEventListener('click', () => {
        this.state.uploadedPhotos.splice(parseInt(btn.dataset.photoIdx, 10), 1);
        this.renderPhotoPreview();
      });
    });
  }

  async submitReview() {
    this.hideFormError();
    const form = this.querySelector('.cr-form');
    if (!form) return;

    const rating = this.state.formRating;
    const title = form.querySelector('[name="title"]')?.value.trim();
    const reviewBody = form.querySelector('[name="body"]')?.value.trim();
    const customerName = form.querySelector('[name="name"]')?.value.trim();
    const customerEmail = form.querySelector('[name="email"]')?.value.trim();
    const customerState = form.querySelector('[name="state"]')?.value.trim();

    if (!rating) { this.showFormError('Please select a star rating.'); return; }
    if (!customerName) { this.showFormError('Please enter your name.'); return; }
    if (customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      this.showFormError('Please enter a valid email address.');
      return;
    }
    if (!title) { this.showFormError('Please enter a review title.'); return; }
    if (!reviewBody || reviewBody.length < 50) {
      this.showFormError('Review must be at least 50 characters.');
      return;
    }

    const submitBtn = form.querySelector('.cr-form__submit');
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Submitting...'; }

    let turnstileToken = '';
    if (window.turnstile && this.turnstileSiteKey) {
      try {
        const widgetEl = this.querySelector('.cf-turnstile');
        if (widgetEl) turnstileToken = turnstile.getResponse(widgetEl);
      } catch { /* proceed without */ }
    }

    try {
      const res = await fetch(`${this.apiBase}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: this.productId,
          productHandle: this.productHandle,
          productTitle: this.productTitle,
          customerName,
          customerEmail,
          customerState,
          rating,
          title,
          reviewBody,
          photoUrls: this.state.uploadedPhotos,
          turnstileToken,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        this.showFormSuccess(data.message);
        setTimeout(() => {
          this.closeForm();
          this.resetForm();
          if (data.status === 'approved') {
            this.state.page = 1;
            this.loadStats();
            this.loadReviews();
          }
        }, 2000);
      } else {
        this.showFormError(data.error || 'Failed to submit review.');
      }
    } catch {
      this.showFormError('Network error. Please try again.');
    }

    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Submit Review'; }
  }

  resetForm() {
    const form = this.querySelector('.cr-form');
    if (form) form.reset();
    this.state.formRating = 0;
    this.state.uploadedPhotos = [];
    this.updateFormStars();
    this.renderPhotoPreview();
    this.hideFormError();
    const charCount = this.querySelector('.cr-form__char-count');
    if (charCount) charCount.textContent = '0/5000';
  }

  showFormError(msg) {
    const el = this.querySelector('.cr-form__error');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }

  hideFormError() {
    const el = this.querySelector('.cr-form__error');
    if (el) el.style.display = 'none';
    const s = this.querySelector('.cr-form__success');
    if (s) s.style.display = 'none';
  }

  showFormSuccess(msg) {
    const el = this.querySelector('.cr-form__success');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;');
  }

  disconnectedCallback() {
    document.body.style.overflow = '';
  }
}

customElements.define('custom-reviews-widget', CustomReviewsWidget);
